import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Marquee from "@/components/Marquee";
import { LIFESTYLE_IMGS } from "@/lib/constants";

const services = [
  {
    id: "agent",
    eyebrow: "Flagship Certification",
    title: "6-Hour AI Agent Building Certification",
    price: "$199",
    sub: "Live cohort · Official certification badge",
    description:
      "The deep one. Six hours, no filler. You'll leave with a working agent that books appointments, answers DMs, qualifies leads, and runs ops for your business — all while you sleep.",
    bullets: [
      "Prompt engineering frameworks (the same ones used at top AI labs)",
      "Build a custom agent end-to-end with your business data",
      "Plug-and-play prompt libraries and templates",
      "Official Skillhouse AI Architect certification",
      "Private alumni community access",
    ],
    img: LIFESTYLE_IMGS[0],
  },
  {
    id: "animation",
    eyebrow: "Creator Track",
    title: "4-Hour AI Animation Course",
    price: "$149.99",
    sub: "Self-paced · Drop-ready assets",
    description:
      "Made for the creator who's tired of watching everybody else go viral. Learn the AI animation stack that turns flat brand assets into scroll-stopping motion — for reels, ads, and product launches.",
    bullets: [
      "Master AI animation tools (text-to-motion, lip sync, scene gen)",
      "Build a 30-day reel content engine",
      "Brand-safe templates you can edit in minutes",
      "Lifetime access + every update",
      "Bonus: monetization playbook for creators",
    ],
    img: LIFESTYLE_IMGS[1],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section
        className="relative bg-[#0A0A0A] text-white overflow-hidden grain"
        data-testid="services-header"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20 md:pb-28">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] font-bold mb-6 fade-up">
            What We Teach
          </p>
          <h1 className="font-display uppercase leading-[0.88] text-6xl md:text-8xl lg:text-9xl max-w-5xl fade-up delay-1">
            Secure your <span className="hi-yellow">bag</span> with AI.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg md:text-xl leading-relaxed fade-up delay-2">
            Two flagship trainings designed to replace your operations team
            with automation — and make your brand look like it has a $10K
            creative agency on payroll.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up delay-3">
            <Link
              to="/book"
              data-testid="services-header-cta"
              className="inline-flex items-center gap-2 bg-[#FFD60A] text-black px-7 py-5 font-bold uppercase tracking-[0.2em] text-sm hover:bg-white transition-colors"
            >
              Book a Free Consult <ArrowUpRight className="w-5 h-5" />
            </Link>
            <a
              href="#service-agent"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-7 py-5 font-bold uppercase tracking-[0.2em] text-sm hover:bg-white hover:text-black transition-colors"
              data-testid="services-header-scroll"
            >
              See Curriculum
            </a>
          </div>
        </div>
      </section>

      <Marquee variant="yellow" items={["Built for Black Women", "Real Certifications", "Real Curriculum", "Real Receipts", "No Fluff"]} />

      {/* SERVICES — ALTERNATING */}
      <section className="bg-[#FAFAFA]" data-testid="services-detail">
        {services.map((s, i) => {
          const reversed = i % 2 === 1;
          return (
            <div
              key={s.id}
              id={`service-${s.id}`}
              className="py-20 md:py-28 border-b border-[#E5E5E5] last:border-b-0"
              data-testid={`service-block-${s.id}`}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
                <div className={`md:col-span-6 ${reversed ? "md:order-2" : ""}`}>
                  <div className="relative">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-[420px] md:h-[560px] object-cover"
                    />
                    <div className={`absolute -bottom-5 ${reversed ? "-right-5" : "-left-5"} hidden md:block bg-black text-[#FFD60A] px-5 py-3`}>
                      <p className="font-display text-2xl uppercase">{s.price}</p>
                    </div>
                  </div>
                </div>

                <div className={`md:col-span-6 ${reversed ? "md:order-1" : ""}`}>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold mb-4">
                    {s.eyebrow}
                  </p>
                  <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95]">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-sm uppercase tracking-[0.25em] font-bold text-black/60">
                    {s.sub}
                  </p>

                  <p className="mt-6 text-lg leading-relaxed text-black/80 max-w-xl">
                    {s.description}
                  </p>

                  <ul className="mt-8 space-y-3 max-w-xl">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-base text-black/80"
                      >
                        <span className="mt-1.5 inline-flex w-5 h-5 items-center justify-center bg-[#FFD60A] flex-shrink-0">
                          <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-wrap items-end justify-between gap-6 border-t border-[#E5E5E5] pt-6">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.25em] text-black/50 font-bold mb-1">
                        Tuition
                      </p>
                      <p className="font-display text-5xl md:text-6xl leading-none">
                        {s.price}
                      </p>
                    </div>
                    <Link
                      to="/book"
                      data-testid={`service-enroll-${s.id}`}
                      className="inline-flex items-center gap-2 bg-black text-[#FFD60A] px-7 py-4 font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#FFD60A] hover:text-black transition-colors"
                    >
                      Enroll Now <ArrowUpRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* MICRO LIST OF OTHER COURSES */}
      <section className="bg-[#0A0A0A] text-white py-20 md:py-28" data-testid="services-bonus">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] font-bold mb-3">
                Also Available
              </p>
              <h2 className="font-display uppercase text-5xl md:text-6xl leading-[0.95]">
                Short drops, big skills.
              </h2>
            </div>
            <Link
              to="/book"
              data-testid="services-bonus-cta"
              className="inline-flex items-center gap-2 border-2 border-[#FFD60A] text-[#FFD60A] px-6 py-4 font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#FFD60A] hover:text-black transition-colors"
            >
              Ask About Cohorts <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "2-Hour AI Coding Course", price: "$99", desc: "Prompt your way to a working MVP this weekend." },
              { name: "2-Hour AI Storybook Course", price: "$79", desc: "Write & illustrate a kids' book using AI in one sitting." },
              { name: "2-Hour AI Photo + Video", price: "$89", desc: "Studio-grade content without the studio price tag." },
            ].map((m) => (
              <div
                key={m.name}
                className="border border-[#2A2A2A] p-8 hover:border-[#FFD60A] transition-colors"
                data-testid={`mini-course-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-display text-2xl uppercase leading-tight pr-3">{m.name}</h3>
                  <span className="font-display text-2xl text-[#FFD60A]">{m.price}</span>
                </div>
                <p className="text-white/70 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
